import React, { useRef, useState } from 'react';
import { Upload, Download, AlertTriangle, CheckCircle, ArrowRight, Info } from 'lucide-react';
import { Dialog } from './ui/dialog';
import { Button } from './ui/button';
import { parseExcelFile, autoMatch, type ParseResult } from '@/lib/excel';
import {
  tasksApi,
  type ImportPreviewRow,
  type PreviewRowResult,
  type ImportPreviewResponse,
} from '@/lib/api';

// ── Field config ─────────────────────────────────────────────────────────────

interface FieldDef {
  key:        string;
  label:      string;
  required?:  boolean;
}

const FIELDS: FieldDef[] = [
  { key: 'equipment_ref',              label: 'Equipment (asset tag/serial/name)', required: true },
  { key: 'name',                       label: 'PM Name',                            required: true },
  { key: 'frequency_phrase',           label: 'Frequency',                          required: true },
  { key: 'task_type',                  label: 'Task Type' },
  { key: 'technician_ref',             label: 'Assigned Technician' },
  { key: 'estimated_duration_minutes', label: 'Duration (minutes)' },
  { key: 'instructions',               label: 'Instructions / Notes' },
  { key: 'last_completed_at',          label: 'Last Completed' },
  { key: 'next_due_at',                label: 'Next Due' },
  { key: 'is_active',                  label: 'Active?' },
];

const SKIP = '';

type Step = 'upload' | 'map' | 'validate' | 'done';

interface Props {
  onDownloadTemplate: () => void;
  onComplete?:        () => void;
}

// ── Component ────────────────────────────────────────────────────────────────

export function MaintenanceTaskImport({ onDownloadTemplate, onComplete }: Props) {
  const [open, setOpen]         = useState(false);
  const [step, setStep]         = useState<Step>('upload');
  const [parsed, setParsed]     = useState<ParseResult | null>(null);
  const [mapping, setMapping]   = useState<Record<string, string>>({});
  const [preview, setPreview]   = useState<ImportPreviewResponse | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState<{ created: number; errors: Array<{ row_index: number; error: string }> } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStep('upload'); setParsed(null); setMapping({});
    setPreview(null); setCommitResult(null);
    setPreviewing(false); setCommitting(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    const wasDone = step === 'done';
    setOpen(false);
    reset();
    if (wasDone && onComplete) onComplete();
  };

  // ── Step 1: upload ────────────────────────────────────────────────────────

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await parseExcelFile(file);
      setParsed(result);
      if (result.errors.length === 0 && result.rows.length > 0) {
        const auto = autoMatch(FIELDS.map(f => f.key), result.headers);
        setMapping(auto);
        setStep('map');
      }
    } catch (err) {
      setParsed({ headers: [], rows: [], errors: [(err as Error).message] });
    }
  };

  // ── Step 2: map ───────────────────────────────────────────────────────────

  const requiredsMapped = FIELDS.filter(f => f.required).every(f => mapping[f.key] && mapping[f.key] !== SKIP);

  const setFieldMapping = (fieldKey: string, fileCol: string) =>
    setMapping(m => ({ ...m, [fieldKey]: fileCol }));

  const handleValidate = async () => {
    if (!parsed) return;
    setPreviewing(true);
    try {
      const rows: ImportPreviewRow[] = parsed.rows.map((raw, idx) => {
        const row: Partial<ImportPreviewRow> = { row_index: idx };
        for (const f of FIELDS) {
          const col = mapping[f.key];
          if (col && col !== SKIP) {
            const val = (raw[col] ?? '').trim();
            if (val) (row as Record<string, string>)[f.key] = val;
          }
        }
        return row as ImportPreviewRow;
      });

      const res = await tasksApi.importPreview(rows);
      setPreview(res);
      setStep('validate');
    } catch (err) {
      alert(`Preview failed: ${(err as Error).message}`);
    } finally {
      setPreviewing(false);
    }
  };

  // ── Step 3: validate → commit ─────────────────────────────────────────────

  const handleCommit = async () => {
    if (!preview) return;
    // Spread the resolved payload and re-attach row_index from the outer
    // preview row — row_index isn't part of `resolved` (it lives on the
    // outer wrapper) but the commit endpoint requires it for error reporting.
    const importable = preview.rows
      .filter(r => (r.status === 'ready' || r.status === 'warning') && r.resolved)
      .map(r => ({ ...r.resolved!, row_index: r.row_index }));

    if (importable.length === 0) return;

    setCommitting(true);
    try {
      const res = await tasksApi.importCommit(importable);
      setCommitResult(res);
      setStep('done');
    } catch (err) {
      alert(`Import failed: ${(err as Error).message}`);
    } finally {
      setCommitting(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  const stepLabel = (s: Step, n: number, title: string) => (
    <div className="flex items-center gap-2 mb-3">
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0
        ${step === s ? 'bg-brand-600 text-white' : 'bg-slate-200 text-slate-500'}`}>{n}</span>
      <span className={`text-sm font-medium ${step === s ? 'text-slate-900' : 'text-slate-400'}`}>{title}</span>
    </div>
  );

  const statusPill = (r: PreviewRowResult) => {
    if (r.status === 'ready')     return <span className="inline-flex items-center gap-1 text-xs text-green-700"><CheckCircle size={12} /> Ready</span>;
    if (r.status === 'warning')   return <span className="inline-flex items-center gap-1 text-xs text-amber-700"><AlertTriangle size={12} /> Warning</span>;
    if (r.status === 'duplicate') return <span className="inline-flex items-center gap-1 text-xs text-slate-600"><Info size={12} /> Duplicate</span>;
    return <span className="inline-flex items-center gap-1 text-xs text-red-700"><AlertTriangle size={12} /> Error</span>;
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Upload size={14} /> Import PMs
      </Button>

      <Dialog open={open} onClose={handleClose} title="Import PM Schedules" className="max-w-4xl">
        <div className="flex flex-col gap-5">

          {/* ── Step 1: Upload ────────────────────────────────────────────── */}
          <div>
            {stepLabel('upload', 1, 'Upload your file')}
            {step === 'upload' && (
              <div className="flex flex-col gap-3">
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5 flex items-center justify-between gap-3">
                  <p className="text-xs text-slate-500">
                    Don't have a file yet?{' '}
                    <button onClick={onDownloadTemplate} className="text-brand-600 underline underline-offset-2">
                      Download our template
                    </button>{' '}— or upload your own and map the columns in the next step.
                  </p>
                  <Button variant="secondary" size="sm" onClick={onDownloadTemplate}>
                    <Download size={13} /> Template
                  </Button>
                </div>
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg px-4 py-8 cursor-pointer hover:border-brand-400 hover:bg-brand-50/30 transition-colors">
                  <Upload size={24} className="text-slate-400" />
                  <span className="text-sm font-medium text-slate-600">Click to choose .xlsx or .csv</span>
                  <span className="text-xs text-slate-400">Any column names — you'll map them in the next step</span>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={handleFile}
                  />
                </label>
                {parsed?.errors.map((e, i) => (
                  <p key={i} className="text-sm text-red-600 flex items-center gap-1.5">
                    <AlertTriangle size={14} /> {e}
                  </p>
                ))}
              </div>
            )}
            {step !== 'upload' && parsed && (
              <p className="text-xs text-green-700 bg-green-50 rounded px-3 py-1.5 inline-flex items-center gap-1.5">
                <CheckCircle size={13} /> {parsed.rows.length} rows loaded from file
                <button onClick={reset} className="ml-2 text-slate-400 hover:text-slate-600 underline underline-offset-2">
                  Change file
                </button>
              </p>
            )}
          </div>

          {/* ── Step 2: Map ──────────────────────────────────────────────── */}
          {(step === 'map' || step === 'validate' || step === 'done') && (
            <div>
              {stepLabel('map', 2, 'Map your columns to VantixMT fields')}
              {step === 'map' && (
                <div className="flex flex-col gap-3">
                  <p className="text-xs text-slate-500">
                    We auto-detected matches where possible. Fix any that are wrong, or set them to{' '}
                    <span className="font-medium">— skip —</span>.
                  </p>
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 w-1/3">VantixMT field</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 w-8"></th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Your file's column</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {FIELDS.map(f => {
                          const mapped = mapping[f.key] ?? '';
                          const isMatched = mapped !== SKIP && mapped !== '';
                          return (
                            <tr key={f.key} className={f.required && !isMatched ? 'bg-red-50' : ''}>
                              <td className="px-4 py-2">
                                <span className="font-medium text-slate-800">{f.label}</span>
                                {f.required && <span className="text-red-500 ml-0.5 text-xs">*</span>}
                              </td>
                              <td className="px-2 py-2 text-slate-300"><ArrowRight size={14} /></td>
                              <td className="px-4 py-2">
                                <select
                                  value={mapped}
                                  onChange={e => setFieldMapping(f.key, e.target.value)}
                                  className={`w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500
                                    ${isMatched ? 'border-green-300 bg-green-50 text-green-800' : 'border-slate-300 bg-white text-slate-500'}
                                    ${f.required && !isMatched ? 'border-red-300 bg-red-50' : ''}`}
                                >
                                  <option value="">— skip —</option>
                                  {parsed?.headers.map(h => <option key={h} value={h}>{h}</option>)}
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {!requiredsMapped && (
                    <p className="text-xs text-red-600 flex items-center gap-1.5">
                      <AlertTriangle size={13} /> Map all required fields (*) before continuing.
                    </p>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" size="sm" onClick={reset}>Back</Button>
                    <Button size="sm" disabled={!requiredsMapped || previewing} onClick={handleValidate}>
                      {previewing ? 'Validating…' : 'Validate'}
                    </Button>
                  </div>
                </div>
              )}
              {(step === 'validate' || step === 'done') && (
                <div className="rounded-lg border border-slate-200 px-4 py-2 bg-slate-50">
                  <p className="text-xs text-slate-500">
                    {FIELDS.filter(f => mapping[f.key]).length} fields mapped.{' '}
                    <button
                      onClick={() => setStep('map')}
                      className="text-brand-600 underline underline-offset-2"
                      disabled={step === 'done'}
                    >Edit mapping</button>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Validate ─────────────────────────────────────────── */}
          {(step === 'validate' || step === 'done') && preview && (
            <div>
              {stepLabel('validate', 3, step === 'done' ? 'Validation complete' : 'Validate parsed result')}
              <p className="text-sm mb-2">
                <span className="text-green-700 font-medium">{preview.summary.ready} ready</span>
                {preview.summary.warnings > 0 && (
                  <span className="text-amber-700 font-medium ml-3">{preview.summary.warnings} warnings</span>
                )}
                {preview.summary.errors > 0 && (
                  <span className="text-red-700 font-medium ml-3">{preview.summary.errors} errors</span>
                )}
                {preview.summary.duplicates > 0 && (
                  <span className="text-slate-600 font-medium ml-3">{preview.summary.duplicates} duplicates</span>
                )}
              </p>
              <div className="overflow-x-auto rounded-lg border border-slate-200 max-h-80">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0 border-b border-slate-200">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-slate-500 font-medium w-7">#</th>
                      <th className="px-2 py-1.5 text-left text-slate-500 font-medium">Status</th>
                      <th className="px-2 py-1.5 text-left text-slate-500 font-medium">Equipment</th>
                      <th className="px-2 py-1.5 text-left text-slate-500 font-medium">PM Name</th>
                      <th className="px-2 py-1.5 text-left text-slate-500 font-medium">Frequency</th>
                      <th className="px-2 py-1.5 text-left text-slate-500 font-medium">Technician</th>
                      <th className="px-2 py-1.5 text-left text-slate-500 font-medium">Message</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {preview.rows.map(r => (
                      <tr key={r.row_index} className={
                        r.status === 'ready' ? 'bg-green-50/40' :
                        r.status === 'warning' ? 'bg-amber-50/40' :
                        r.status === 'duplicate' ? 'bg-slate-50' : 'bg-red-50/40'
                      }>
                        <td className="px-2 py-1 text-slate-400">{r.row_index + 1}</td>
                        <td className="px-2 py-1">{statusPill(r)}</td>
                        <td className="px-2 py-1 text-slate-700">{r.resolved?.equipment_label ?? '—'}</td>
                        <td className="px-2 py-1 text-slate-700">{r.resolved?.name ?? '—'}</td>
                        <td className="px-2 py-1 text-slate-700">
                          {r.resolved
                            ? r.resolved.frequency_type === 'on_condition'
                              ? 'on condition'
                              : `${r.resolved.frequency_value} ${r.resolved.frequency_type}`
                            : '—'}
                        </td>
                        <td className="px-2 py-1 text-slate-700">{r.resolved?.assigned_technician_label ?? <span className="text-slate-400">unassigned</span>}</td>
                        <td className="px-2 py-1 text-slate-600">
                          {r.error && <span className="text-red-700">{r.error}</span>}
                          {r.warnings && r.warnings.map((w, i) => (
                            <div key={i} className="text-amber-700">{w}</div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {step === 'validate' && (
                <div className="flex justify-end gap-2 mt-3">
                  <Button variant="secondary" size="sm" onClick={() => setStep('map')}>Edit mapping</Button>
                  <Button
                    size="sm"
                    disabled={committing || preview.summary.ready + preview.summary.warnings === 0}
                    onClick={handleCommit}
                  >
                    {committing
                      ? 'Importing…'
                      : `Import ${preview.summary.ready + preview.summary.warnings} ready row${(preview.summary.ready + preview.summary.warnings) !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ── Step 4: Done ────────────────────────────────────────────── */}
          {step === 'done' && commitResult && (
            <div>
              {stepLabel('done', 4, 'Import complete')}
              <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3">
                <p className="text-sm">
                  <span className="text-green-700 font-medium">
                    {commitResult.created} PM{commitResult.created !== 1 ? 's' : ''} imported successfully
                  </span>
                  {commitResult.errors.length > 0 && (
                    <span className="text-red-700 font-medium ml-3">{commitResult.errors.length} failed</span>
                  )}
                </p>
                {commitResult.errors.length > 0 && (
                  <ul className="mt-2 text-xs text-red-700 list-disc list-inside">
                    {commitResult.errors.map(e => (
                      <li key={e.row_index}>Row {e.row_index + 1}: {e.error}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-1 border-t border-slate-100">
            <Button variant="secondary" onClick={handleClose}>
              {step === 'done' ? 'Close' : 'Cancel'}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
